import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";

export function useIsTextTruncated(ref: RefObject<HTMLElement | null>, watchKey?: unknown, enabled = true) {
  const [isTruncated, setIsTruncated] = useState(false);
  const [observedElement, setObservedElement] = useState<HTMLElement | null>(null);
  const previousObservedElementRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const next = enabled ? ref.current : null;
    setObservedElement((current) => (current === next ? current : next));
  });

  useEffect(() => {
    if (!enabled || !observedElement || ref.current !== observedElement) {
      previousObservedElementRef.current = null;
      setIsTruncated(false);
      return;
    }

    if (typeof ResizeObserver === "undefined") return;

    // Turning truncation on remounts the element inside a tooltip wrapper, so this effect reruns
    // against a freshly mounted node. Measuring it synchronously reads a pre-layout 0x0 and would
    // flip isTruncated back off; on a rebind, leave the first measurement to the observer.
    const isRebind =
      previousObservedElementRef.current !== null && previousObservedElementRef.current !== observedElement;
    previousObservedElementRef.current = observedElement;

    const update = () => setIsTruncated(observedElement.scrollWidth > observedElement.clientWidth);

    if (!isRebind) {
      update();
    }

    const observer = new ResizeObserver(update);
    observer.observe(observedElement);
    return () => observer.disconnect();
  }, [enabled, observedElement, watchKey, ref]);

  return isTruncated;
}
